// The open league format, version 1 (DIRECTION A13, OPEN_LEAGUE_FORMAT.md,
// PLAN-2026-10-18-families-and-newcomers §13.1): a fantasy league, or one
// team of it, as one JSON file any site can write and read. This schema is
// the format's definition; the document says what the fields mean.
//
// NO SITE'S IDS. A team is named by a key of the file's own, a manager by
// display name, a player by name, positions, professional team and the
// well-known ids. Never an email, an account id, a private message or chat.
//
// ADDITIVE ONLY within a version: a reader ignores a field it does not know,
// and a field is never renamed or given a new meaning without a new version.

import { z } from 'zod';
import { StoredLeagueSettings } from '../rules/schemas';
import { ChosenBadge, isKnownBadge } from '../teams/badges';
import { LeagueRef } from '../api/views';

export const OPEN_LEAGUE_FORMAT = 'open-league';
export const OPEN_LEAGUE_VERSION = 1;

const Hex = z.string().regex(/^#[0-9a-fA-F]{6}$/);
/** The file's own name for a team: unique in the file, nobody's database id. */
const TeamKey = z.string().min(1).max(64);
const Count = z.number().int().min(0);

/** A player, as any site can match him: by `ids` first, then by name and position. */
export const OpenPlayer = z.object({
  name: z.string().min(1).max(120),
  positions: z.array(z.string().min(1)).max(8),
  /** The professional team's abbreviation; null for a free agent. */
  proTeam: z.string().nullable(),
  /** A source's id for him, by source: `gsis` for the league office's own. */
  ids: z.record(z.string(), z.string()).default({}),
});
export type OpenPlayer = z.infer<typeof OpenPlayer>;

/**
 * A team's badge as a file carries it: one of this site's (`ChosenBadge`), or
 * one this reader cannot draw (a face with a part it does not have, a kind
 * from another site), which is read and then left off by `badgeFromFile`, as
 * a reader of the format leaves off a badge it does not have. So a face
 * never fails a whole file (B-488, added 2026-10-24).
 */
export const OpenBadge = z.union([ChosenBadge, z.looseObject({ kind: z.string() })]);

/** A file's badge, when it is one this site can draw; null for one it leaves off. */
export function badgeFromFile(badge: OpenTeam['badge']): ChosenBadge {
  const parsed = ChosenBadge.safeParse(badge);
  return parsed.success && parsed.data !== null && isKnownBadge(parsed.data) ? parsed.data : null;
}

export const OpenTeam = z.object({
  key: TeamKey,
  name: z.string().min(1).max(60),
  abbreviation: z.string().max(6),
  /** The manager's display name; null for a team nobody runs. Never an address or an id. */
  manager: z.string().max(60).nullable(),
  coManagers: z.array(z.string().max(60)).default([]),
  colors: z.object({ primary: Hex, secondary: Hex }).nullable().default(null),
  badge: OpenBadge.default(null),
  /** The players on the team now. In a team file, only the file's own team has one. */
  roster: z
    .array(
      z.object({
        player: OpenPlayer,
        acquired: z
          .enum(['draft', 'waiver', 'free-agent', 'trade', 'commissioner', 'keeper'])
          .nullable()
          .default(null),
      }),
    )
    .default([]),
});
export type OpenTeam = z.infer<typeof OpenTeam>;

export const OpenStanding = z.object({
  team: TeamKey,
  rank: z.number().int().min(1),
  wins: Count,
  losses: Count,
  ties: Count,
  pointsFor: z.number(),
  pointsAgainst: z.number().nullable().default(null),
  /** Made the playoffs; null where it was not kept. */
  playoffs: z.boolean().nullable().default(null),
});
export type OpenStanding = z.infer<typeof OpenStanding>;

export const OpenGame = z.object({
  week: z.number().int().min(1),
  kind: z.enum(['regular', 'playoff', 'consolation']),
  /** Null only for a week a team plays against the field rather than an opponent. */
  home: TeamKey.nullable(),
  away: TeamKey.nullable(),
  homeScore: z.number().nullable(),
  awayScore: z.number().nullable(),
  final: z.boolean(),
  /** Null for a tie, or a game not yet final. */
  winner: TeamKey.nullable(),
});
export type OpenGame = z.infer<typeof OpenGame>;

export const OpenPick = z.object({
  overall: z.number().int().min(1),
  round: z.number().int().min(1),
  /** The team that made the pick. */
  team: TeamKey,
  player: OpenPlayer.nullable(),
  /** An auction's price; null in a draft of picks. */
  price: z.number().int().min(0).nullable().default(null),
});
export type OpenPick = z.infer<typeof OpenPick>;

/** One team's side of a move. A move taken back is not in a file. */
export const OpenMove = z.object({
  at: z.string(),
  week: z.number().int().min(0),
  kind: z.enum(['add', 'drop', 'claim', 'trade']),
  team: TeamKey,
  added: z.array(OpenPlayer).default([]),
  dropped: z.array(OpenPlayer).default([]),
  /** A waiver claim's winning bid; null otherwise. */
  bid: z.number().nullable().default(null),
});
export type OpenMove = z.infer<typeof OpenMove>;

export const OpenSeason = z.object({
  season: z.number().int().min(1900).max(3000),
  /** True once its champion is decided. */
  complete: z.boolean(),
  champion: TeamKey.nullable(),
  runnerUp: TeamKey.nullable().default(null),
  standings: z.array(OpenStanding),
  /** None for a season kept only as its table. */
  games: z.array(OpenGame).default([]),
  draft: z.array(OpenPick).default([]),
  moves: z.array(OpenMove).default([]),
});
export type OpenSeason = z.infer<typeof OpenSeason>;

export const OpenLeagueFile = z
  .object({
    format: z.literal(OPEN_LEAGUE_FORMAT),
    version: z.literal(OPEN_LEAGUE_VERSION),
    exportedAt: z.string(),
    /** The site that wrote it, by name; null when it does not say. */
    generator: z.string().max(120).nullable().default(null),
    scope: z.enum(['league', 'team']),
    /** With `scope: team`, the key of the team the file is about. */
    team: TeamKey.nullable().default(null),
    league: z.object({
      name: z.string().min(1).max(60),
      sport: z.string().min(1),
      season: z.number().int().min(1900).max(3000),
      /**
       * The rules as this site keeps them (`LeagueSettings`). Optional: a site
       * with rules of its own leaves them out, and a reader uses its defaults.
       */
      rules: StoredLeagueSettings.optional(),
      teams: z.array(OpenTeam).min(1).max(32),
      /** Oldest first. */
      seasons: z.array(OpenSeason).default([]),
    }),
  })
  .superRefine((file, ctx) => {
    // EVERY TEAM NAMED IS A TEAM IN THE FILE, and every key names one team.
    const keys = new Set<string>();
    for (const t of file.league.teams) {
      if (keys.has(t.key))
        ctx.addIssue({ code: 'custom', message: `Two teams share the key "${t.key}".` });
      keys.add(t.key);
    }
    const named = (key: string | null, where: string) => {
      if (key !== null && !keys.has(key))
        ctx.addIssue({
          code: 'custom',
          message: `${where} names "${key}", which is not a team in the file.`,
        });
    };
    if (file.scope === 'team') {
      if (file.team === null)
        ctx.addIssue({ code: 'custom', message: 'A team file names its team.' });
      else named(file.team, 'The file');
    }
    for (const s of file.league.seasons) {
      const where = `The ${s.season} season`;
      named(s.champion, where);
      named(s.runnerUp, where);
      for (const r of s.standings) named(r.team, where);
      for (const g of s.games) {
        named(g.home, where);
        named(g.away, where);
        named(g.winner, where);
      }
      for (const p of s.draft) named(p.team, where);
      for (const m of s.moves) named(m.team, where);
    }
  });
export type OpenLeagueFile = z.infer<typeof OpenLeagueFile>;

/** A JSON Schema document, as `GET /api/formats/open-league` serves the format's. */
export const FormatSchema = z.record(z.string(), z.unknown());
export type FormatSchema = z.infer<typeof FormatSchema>;

/**
 * `POST /api/leagues/import` — a new league here from a league's file
 * (PLAN-2026-10-18 §13.1): the file, which of its teams is the person's
 * own, and the season and time zone to play in, as the create form asks.
 */
export const ImportLeagueRequest = z.object({
  file: OpenLeagueFile,
  /** The key of the importing person's own team in the file. */
  myTeam: TeamKey,
  season: z.number().int().min(2020).max(2100),
  timeZone: z.string().trim().min(1).max(64),
});
export type ImportLeagueRequest = z.infer<typeof ImportLeagueRequest>;

/** What an import made, and what it left behind and why. */
export const ImportLeagueResult = z.object({
  league: LeagueRef,
  /** `kept`: the file's rules, fitted to this season's weeks; `default`: they did not fit. */
  rules: z.enum(['kept', 'default']),
  /** The past seasons put in the record book. */
  seasons: z.number().int().min(0),
  /** Sentences on what the league did not take from the file, and why. */
  notes: z.array(z.string()),
});
export type ImportLeagueResult = z.infer<typeof ImportLeagueResult>;
